import type { LoginRefreshResponse } from '@/types/responses/loginRefreshResponse'

export const useAuth = () => {
  const clearCookies = () => {
    const authStore = useAuthStore()
    const petsDetailsStore = usePetsDetailsStore()
    const policyStore = usePolicyStore()
    const userStore = useUserStore()

    // Clear authentication flag FIRST to prevent redirect loop
    // where middleware sees isAuthenticated=true and redirects away from /login
    userStore.clearAuth()

    authStore.$reset()
    petsDetailsStore.$reset()
    policyStore.$reset()
    // Note: userStore.$reset() (full user object reset) is still handled in
    // logoutUser() via activePinia._s.forEach to avoid a race condition with
    // cookie rehydration. clearAuth() only flips isAuthenticated to false.
  }

  const fetchRefreshToken = async (): Promise<boolean> => {
    const { refreshToken, setTokens } = useAuthStore()
    const { getPolicies, setSelectedPolicy } = usePolicyStore()

    if (!refreshToken) {
      const { logSessionEvent } = useSessionAudit()
      logSessionEvent('session:token_refresh:no_refresh_token')
      return false
    }

    // Attempt refresh with one retry for transient errors (5xx, network).
    // The backend's anti-hijack check deletes both tokens if a refresh is
    // attempted while the access token hasn't expired, so we must NOT retry
    // on 401/403 — those are genuine auth rejections, not transient hiccups.
    const maxAttempts = 2

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const { response } = useApiFetch('user/login/refresh', {
          method: 'POST',
          body: { refreshToken }
        })

        const { bearerToken, refreshToken: newRefreshToken }: LoginRefreshResponse = await response

        setTokens({ bearerToken, refreshToken: newRefreshToken })

        const policies = await getPolicies()

        if (policies.length) {
          setSelectedPolicy(policies[0])
        }

        return true
      } catch (error: any) {
        const status = error?.status || error?.statusCode || error?.response?.status

        // 401/403 = genuine auth failure, don't retry
        if (status === 401 || status === 403) {
          console.error('Token refresh rejected by server:', status)
          return false
        }

        // Transient error (5xx, network) — retry after a short delay
        if (attempt < maxAttempts) {
          console.warn(`Token refresh attempt ${attempt} failed (status: ${status}), retrying...`)
          await new Promise((resolve) => setTimeout(resolve, 1000))
          continue
        }

        console.error('Token refresh failed after retries:', error)
        return false
      }
    }

    return false
  }

  const loginRedirect = async ({ redirectQuery = false }: { redirectQuery?: boolean } = {}): Promise<
    ReturnType<typeof navigateTo>
  > => {
    const route = useRoute()

    return navigateTo({
      path: '/login',
      ...(redirectQuery && !['/', '/home', '/login'].includes(route.path)
        ? { query: { redirect: route.fullPath } }
        : {})
    })
  }

  return {
    clearCookies,
    fetchRefreshToken,
    loginRedirect
  }
}
